// EditableField.tsx
import React, { useEffect, useState } from "react";


export type EditableFieldType = "text" | "date" | "select" | "checkbox" | "number";
interface FieldDefinition {
  label: string;
  key: string;
  type?: EditableFieldType;
  optionsKey?: string;
}

const EditableField = ({
  label,
  fieldKey,
  value,
  onUpdate,
  metadata,
  optionsKey,
  type = "text",
}: {
  label: string;
  fieldKey: string;
  value: any;
  onUpdate: (updated: Record<string, any>) => void;
  metadata?: Record<string, string[]>;
  optionsKey?: string;
  type?: EditableFieldType;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
    if (inputValue !== value) {
      onUpdate({ [fieldKey]: inputValue });
    }
  };

  const options = optionsKey ? metadata?.[optionsKey] || [] : [];

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>

      {isEditing ? (
        type === "select" ? (
          <select
            className="w-full px-2 py-1 border-b-2 border-blue-500 focus:outline-none"
            value={inputValue || ""}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            autoFocus
          >
            <option value="" disabled>
              Select
            </option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : type === "date" ? (
          <input
            type="date"
            className="w-full px-2 py-1 border-b-2 border-blue-500 focus:outline-none"
            value={inputValue ? inputValue.substring(0, 10) : ""}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            autoFocus
          />
        ) : type === "checkbox" ? (
          <input
            type="checkbox"
            checked={!!inputValue}
            onChange={(e) => {
              setInputValue(e.target.checked);
              onUpdate({ [fieldKey]: e.target.checked });
            }}
          />
        ) : type === "number" ? (
          <input
            type="number"
            className="w-full px-2 py-1 border-b-2 border-blue-500 focus:outline-none"
            value={inputValue || ""}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={() => {
              setIsEditing(false);
              const numValue = Number(inputValue);
              if (!isNaN(numValue) && numValue !== value) {
                onUpdate({ [fieldKey]: numValue });
              }
            }}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleBlur()}
          />
        ) : (
          <input
            type="text"
            className="w-full px-2 py-1 border-b-2 border-blue-500 focus:outline-none"
            value={inputValue || ""}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleBlur()}
          />
        )
      ) : (
        <div
          className="text-gray-900 font-medium py-1 cursor-pointer hover:text-blue-600 transition-colors truncate"
          onClick={() => setIsEditing(true)}
        >
          {type === "checkbox" ? (
            <input type="checkbox" checked={!!value} readOnly />
          ) : (
            value || <span className="text-gray-400 italic">-</span>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableField;
